import { AuthorAvatar } from '@components/ArtBoard/Threads/components/AuthorAvatar'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { activityHistoryActions } from '@store/reducers/manager/activityHistory/actions'
import type { ActivityHistoryAction } from '@store/reducers/manager/activityHistory/types'
import type React from 'react'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import type { AppState, UiPreview } from '@/types'
import { clsJoin } from '@/utils'
import { formatTime } from '../../../../helper'
import { ChangesItem, UserItemMenu } from './components'
import css from './styles.module.css'

type Props = {
  id: string
  authorUuid: string
  actions: ActivityHistoryAction[]
  projectId: string
  changesIsOpen: string | null
  singlePreview: string | null
  onSetChangesIsOpen: (id: string | null) => void
}

export const UserItem: React.FC<Props> = ({
  id,
  authorUuid,
  actions,
  changesIsOpen,
  singlePreview,
  onSetChangesIsOpen,
}) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const recipients = useSelector(migrationProjectSelector.getProjectRecipients)

  const { selectedPreview, currentPreview } = useSelector<AppState, UiPreview>(
    (state) => state.ui.preview
  )

  useEffect(() => {
    if (singlePreview) {
      if (actions.some((action) => action.id === +singlePreview)) {
        onSetChangesIsOpen(id)
      }
    }
  }, [singlePreview, actions])

  const getRecipient = (userId: string) => {
    return recipients.find((recipient) => recipient.id === userId)
  }

  const author = getRecipient(authorUuid)
  const lastActionId = actions[0].id
  const lastActionTime = actions[0].createdAt
  const actionIsCurrentPreview = false

  const onPreview = (id: number) => {
    dispatch(activityHistoryActions.previewSaga({ id }))
  }

  const onRestore = (id: number) => {
    sendGtmEvent(GtmTriggerName.PROJECT_RESTORE)
    dispatch(activityHistoryActions.restoreSaga({ id }))
  }

  const timeOrText = actionIsCurrentPreview
    ? 'Current version'
    : formatTime(lastActionTime)

  const active =
    selectedPreview === lastActionId || currentPreview === lastActionId

  return (
    <>
      <div className={clsJoin(css.user_item, active && css.active)}>
        <div
          className={css.user_content}
          onClick={() => onPreview(lastActionId)}
        >
          <AuthorAvatar author={author} size="md" />
          <div className={css.user_wrap}>
            <div className={css.user_name}>
              {!author ? 'Guest' : author.fullName}
            </div>
            <div className={css.user_bottom_block}>
              <span>{timeOrText}</span>
              <span className={css.divider_dot} />
              <span
                className={clsJoin(
                  css.changes_count,
                  id === changesIsOpen && css.active
                )}
              >
                {actions.length} changes
              </span>
            </div>
          </div>
        </div>

        <UserItemMenu
          id={id}
          buttonCn={css.dropdown_menu}
          changesShown={id === changesIsOpen}
          onSetChangesIsOpen={onSetChangesIsOpen}
          onRestore={() => onRestore(lastActionId)}
        />
      </div>

      {id === changesIsOpen &&
        actions.map((action) => (
          <ChangesItem
            key={action.id}
            {...action}
            onClick={() => onPreview(action.id)}
            onRestore={onRestore}
          />
        ))}
    </>
  )
}
